import { Box } from '@mui/material';
import DashboardLayout from '@examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from '@examples/Navbars/DashboardNavbar';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EventSourceMessage } from '@microsoft/fetch-event-source';
import { useSearchParams } from 'react-router-dom';
import { authStorage } from 'functions/auth-session';
import { refreshSession } from 'api/axios-instance';
import { useMaterialUIController } from 'context';
import { useAlert } from '@components/showAlert/useAlertHook';
import { apiErrorMessage } from '@functions/api-error-message';
import { getCrmColors } from '../CRM/kanban-board/crm-theme';
import { MultichannelList } from './components/multichannel-list';
import { MultichannelConversationPanel } from './components/multichannel-conversation';
import { MultichannelLeadPanel } from './components/multichannel-lead-panel';
import { MultichannelLead } from './services/responses/multichannel.response';
import { resolve } from './components/channel-identity';
import { MultichannelService } from './services/multichannel.service';
import { MultichannelConversation } from './services/responses/multichannel.response';

interface Props {
    /// Cada item do menu do módulo entra com o seu canal; sem canal, a tela mostra a caixa inteira.
    channel?: string;
}

export const Multichannel = ({ channel }: Props) => {
    const { t } = useTranslation();
    const [controller] = useMaterialUIController();
    const cc = getCrmColors(controller.darkMode);
    const { showAlert } = useAlert();
    const [searchParams] = useSearchParams();

    // O card do lead manda para ca com a conversa dele na URL.
    const conversationFromUrl = searchParams.get('conversa');

    const [conversations, setConversations] = useState<MultichannelConversation[]>([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [selected, setSelected] = useState<MultichannelConversation | null>(null);

    // Muda quando chega evento da conversa aberta e recarrega o painel sem piscar a tela.
    const [signal, setSignal] = useState(0);

    /// O painel do lead já carrega estes dados; guardar aqui poupa uma chamada para as variáveis.
    const [lead, setLead] = useState<MultichannelLead | null>(null);

    const load = useCallback(async () => {
        try {
            const reply = await new MultichannelService().conversations(channel);
            const list = reply.data.data ?? [];

            setConversations(list);

            // A conversa aberta é cópia do item da lista: sem trocá-la, o can_reply só atualiza no clique.
            setSelected((current) =>
                current ? list.find((c) => c.id === current.id) ?? current : current);

            if (conversationFromUrl) {
                const target = list.find((c) => String(c.id) === conversationFromUrl);

                if (target) setSelected(target);
            }
        } catch (err) {
            showAlert(apiErrorMessage(err), 'error', 3000);
        } finally {
            setLoading(false);
        }
    }, [channel, conversationFromUrl]);

    /// Abrir é ler: o contador some na hora, sem esperar o Chatwoot, pois o número já é falso.
    const openConversation = useCallback((conversation: MultichannelConversation) => {
        setSelected(conversation);

        if ((conversation.unreadCount ?? 0) === 0) return;

        setConversations((list) => list.map((item) =>
            item.id === conversation.id ? { ...item, unreadCount: 0 } : item));

        // Marcar como lida é acessório: se falhar, o contador volta na próxima carga da lista.
        new MultichannelService().markAsRead(conversation.id).catch(() => {});
    }, []);

    // Trocar de canal é entrar em outra caixa: a conversa aberta e a busca anterior não valem mais.
    // O ouvinte é criado uma vez; sem estas referências enxergaria sempre o estado do primeiro render.
    const loadRef = useRef(load);
    const selectedRef = useRef<number | null>(null);

    useEffect(() => {
        loadRef.current = load;
    }, [load]);

    useEffect(() => {
        selectedRef.current = selected?.id ?? null;
    }, [selected]);

    // Quem avisa que chegou mensagem e o servidor, via webhook do Chatwoot.
    useEffect(() => {
        // Cada recibo de entrega e leitura é um evento; sem juntar rajadas, seriam dezenas de recargas.
        let batchTimer: ReturnType<typeof setTimeout> | null = null;
        let touchedSelected = false;

        const abort = new AbortController();
        let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
        let delay = 2000;
        let stopped = false;

        const connect = async () => {
            if (stopped) return;

            // Token vencido nunca abre o stream: renova antes, senão a tela insiste com 401.
            const session = authStorage.read();

            if (session && session.expiresAt <= Date.now()) {
                try {
                    await refreshSession();
                } catch {
                    // A próxima requisição da tela derruba a sessão e volta ao login.
                }

                if (stopped) return;
            }

            const onEvent = (event: EventSourceMessage) => {
                if (event.event !== 'mensagem') return;

                try {
                    const payload = JSON.parse(event.data);

                    if (payload.conversationId === selectedRef.current) {
                        touchedSelected = true;
                    }
                } catch {
                    // Evento malformado não derruba a tela: a lista recarrega do mesmo jeito logo abaixo.
                }

                if (batchTimer) clearTimeout(batchTimer);

                batchTimer = setTimeout(() => {
                    loadRef.current();

                    if (touchedSelected) {
                        setSignal((value) => value + 1);
                        touchedSelected = false;
                    }
                }, 300);
            };

            // Stream encerrado (401, API reiniciando): reconecta com espera crescente.
            const reconnect = () => {
                if (stopped) return;

                reconnectTimer = setTimeout(connect, delay);
                delay = Math.min(delay * 2, 30000);
            };

            new MultichannelService()
                .stream({ onOpen: () => { delay = 2000; }, onEvent }, abort.signal)
                .then(reconnect, reconnect);
        };

        connect();

        return () => {
            stopped = true;

            if (batchTimer) clearTimeout(batchTimer);
            if (reconnectTimer) clearTimeout(reconnectTimer);

            abort.abort();
        };
    }, []);

    useEffect(() => {
        setLoading(true);
        setSelected(null);
        setSearch('');
        load();
    }, [load]);

    const pageTitle = channel
        ? `${t('crmMultichannel.titulo')} · ${resolve(channel).label}`
        : t('crmMultichannel.titulo');

    return (
        <DashboardLayout>
            <DashboardNavbar title={pageTitle} />

            <Box
                sx={{
                    display: 'flex',
                    height: 'calc(100vh - 170px)',
                    background: cc.pageBg,
                    border: `1px solid ${cc.border}`,
                    borderRadius: 3,
                    overflow: 'hidden',
                }}
            >
                <MultichannelList
                    conversations={conversations}
                    loading={loading}
                    selectedId={selected?.id ?? null}
                    search={search}
                    onSearchChange={setSearch}
                    onSelect={openConversation}
                />

                <MultichannelConversationPanel
                    lead={lead}
                    conversation={selected}
                    signal={signal}
                    onSent={load}
                />

                {selected && (
                    <MultichannelLeadPanel
                        conversation={selected}
                        signal={signal}
                        onLoaded={setLead}
                    />
                )}
            </Box>
        </DashboardLayout>
    );
};
